import { z } from 'zod';

import { SettingScopeFieldsSchema, refineSettingScope } from '../setting-scope/setting-scope';
import { SettingValueTranslationsSchema } from '../setting-value/setting-value';

/**
 * Writing one scope of one setting.
 *
 * `value` is `unknown` **by necessity**: its type lives in the definition's Zod
 * schema, which only the api holds (the registry is server-side). The value is
 * parsed against that schema on arrival and a mismatch is a 422 — so this is an
 * untyped boundary being handed its type one layer in, exactly the case U4
 * permits, not a missing type.
 *
 * `null` is a legitimate value (`ai.cache.ttlSeconds: null` means "cache
 * forever"), so presence is judged by `undefined`, never by nullishness.
 */
export const WriteSettingSchema = SettingScopeFieldsSchema.extend({
  // `.optional()` is required even for `unknown`: zod treats an object key as
  // mandatory unless the schema says otherwise, so a translatable write (which
  // sends `translations` and no `value`) would otherwise be rejected.
  value: z.unknown().optional(),
  /** Present instead of `value` when the definition is translatable (N5). */
  translations: SettingValueTranslationsSchema.optional(),
})
  .strict()
  .superRefine((data, ctx) => {
    refineSettingScope(data, ctx);

    const hasValue = data.value !== undefined;
    const hasTranslations = data.translations !== undefined;

    if (!hasValue && !hasTranslations) {
      ctx.addIssue({
        code: 'custom',
        path: ['value'],
        message: 'A write carries either a value or a translations map.',
      });
      return;
    }

    if (hasValue && hasTranslations) {
      // Accepting both would mean silently dropping one of them, and which one
      // depends on whether the definition happens to be translatable.
      ctx.addIssue({
        code: 'custom',
        path: ['value'],
        message: 'A write carries a value or a translations map, never both.',
      });
    }
  });

export type WriteSettingRequest = z.infer<typeof WriteSettingSchema>;

/**
 * Clearing one scope's override — inheritance visibly returns to global, or to
 * the coded default when the global row is the one cleared.
 */
export const ClearSettingSchema = SettingScopeFieldsSchema.strict().superRefine(refineSettingScope);

export type ClearSettingRequest = z.infer<typeof ClearSettingSchema>;
